import { TrackedCard, KtStagingArea, KtShelf } from '../../redux/api/kit-staging';
import { suggestShelfForArea } from '../../pages/KitStaging/boards/shelfSuggest';

export interface CartStop {
	card: TrackedCard;
	destinationArea: KtStagingArea | null;
	destinationShelf: KtShelf | null;
	isPending: boolean;
	stopNumber: number;
}

function findShelfById(areas: KtStagingArea[], shelfId: number): KtShelf | null {
	for (const a of areas) {
		for (const r of a.racks) {
			const s = r.shelves.find((x) => x.id === shelfId);
			if (s) return s;
		}
	}
	return null;
}

function findAreaForShelf(areas: KtStagingArea[], shelfId: number): KtStagingArea | null {
	return areas.find((a) => a.racks.some((r) => r.shelves.some((s) => s.id === shelfId))) ?? null;
}

/** Compute the cart route stops from all tracked cards + staging areas.
 *  Pure — no side effects. Stops are sorted by destination route_order then OLI,
 *  numbered 1..N. (Fastlane OLI is a UUID string, so the tie-break sort uses
 *  localeCompare rather than numeric subtraction.) */
export function deriveCartStops(cards: TrackedCard[], areas: KtStagingArea[]): CartStop[] {
	const onCart = cards.filter((c) => c.current_area === 'cart');

	const raw: Omit<CartStop, 'stopNumber'>[] = onCart.map((card) => {
		if (card.pending_target_area && card.pending_target_shelf != null) {
			const shelf = findShelfById(areas, card.pending_target_shelf);
			const owningArea = findAreaForShelf(areas, card.pending_target_shelf);
			return { card, destinationArea: owningArea, destinationShelf: shelf, isPending: true };
		}
		const suggestion = suggestShelfForArea(card, areas, card.item_type === 'kit' ? 'jsa' : 'msa');
		return {
			card,
			destinationArea: suggestion?.area ?? null,
			destinationShelf: suggestion?.shelf ?? null,
			isPending: false,
		};
	});

	raw.sort((a, b) => {
		const ra = a.destinationArea?.route_order ?? Number.POSITIVE_INFINITY;
		const rb = b.destinationArea?.route_order ?? Number.POSITIVE_INFINITY;
		if (ra !== rb) return ra - rb;
		return a.card.order_line_item_id.localeCompare(b.card.order_line_item_id);
	});

	return raw.map((s, i) => ({ ...s, stopNumber: i + 1 }));
}

export interface CartStopGroup {
	oli: string;
	stopNumber: number;
	stops: CartStop[]; // one or two entries: kit and/or material on this cart
}

/** Combine per-card cart stops into per-OLI groups, preserving sort order
 *  (group by the earliest stopNumber of its members). */
export function groupCartStopsByOli(stops: CartStop[]): CartStopGroup[] {
	const byOli = new Map<string, CartStop[]>();
	for (const s of stops) {
		const oli = s.card.order_line_item_id;
		const list = byOli.get(oli) ?? [];
		list.push(s);
		byOli.set(oli, list);
	}
	const groups: CartStopGroup[] = [];
	let counter = 1;
	// Walk the original (sorted) order so the group's stopNumber follows the
	// earliest physical stop in the list.
	const seen = new Set<string>();
	for (const s of stops) {
		const oli = s.card.order_line_item_id;
		if (seen.has(oli)) continue;
		seen.add(oli);
		const groupStops = byOli.get(oli) ?? [];
		// Order kit before material inside the group so the visual is consistent.
		groupStops.sort((a, b) => {
			if (a.card.item_type === b.card.item_type) return 0;
			return a.card.item_type === 'kit' ? -1 : 1;
		});
		groups.push({ oli, stopNumber: counter++, stops: groupStops });
	}
	return groups;
}
